import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAppContext } from 'Main';
import OAuthCallback from 'components/authentication/OAuthCallback';
import ForgetPassword from 'components/authentication/simple/ForgetPassword';
import SimpleLogin from 'components/authentication/simple/Login';
import PasswordReset from 'components/authentication/simple/PasswordReset';
import SimpleRegistration from 'components/authentication/simple/Registration';
import Error404 from 'components/errors/Error404';
import OfflineFallback from 'components/offline/OfflineFallback';
import { UserContextProvider } from 'context/UserContext';
import is from 'is_js';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import MainLayout from 'layouts/MainLayout';
import AuthRoute from 'middleware/AuthRoute';
import HasClassroom from 'middleware/HasClassroom';
import IsPrefect from 'middleware/IsPrefect';
import Account from 'pages/account/Account';
import ClassroomRouter from 'pages/classroom/ClassroomRouter';
import CreateClassroom from 'pages/classroom/CreateClassroom';
import Faq from 'pages/faq/Faq';
import Feed from 'pages/feed/Feed';
import Guide from 'pages/guide/Guide';
import AddPoints from 'pages/players/AddPoints';
import PlayerPage from 'pages/players/PlayerPage';
import Ranking from 'pages/ranking/Ranking';
import Team from 'pages/team/Team';
import TeamRouter from 'pages/team/TeamRouter';
import React, { useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnmount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 0
    }
  }
});

const App = () => {
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition }
  } = useAppContext();

  useEffect(() => {
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    if (is.safari()) {
      HTMLClassList.add('safari');
    }
  }, [HTMLClassList]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <QueryClientProvider client={queryClient}>
        <UserContextProvider>
          <Routes>
            <Route path="/offline" element={<OfflineFallback />} />
            <Route element={<AuthSimpleLayout />}>
              <Route path="/login" element={<SimpleLogin />} />
              <Route path="/register" element={<SimpleRegistration />} />
              <Route path="/oauth-callback" element={<OAuthCallback />} />
              <Route path="/forgot-password" element={<ForgetPassword />} />
            </Route>
            <Route element={<AuthRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<Navigate to="/classe" replace />} />
                <Route path="/classe">
                  <Route path="" element={<ClassroomRouter />} />
                  <Route path="nuova" element={<CreateClassroom />} />
                </Route>
                <Route path="/account" element={<Account />} />
                <Route element={<HasClassroom />}>
                  <Route path="/team" element={<TeamRouter />} />
                  <Route path="/team/:username" element={<Team other />} />
                  <Route path="/ranking" element={<Ranking />} />
                  <Route path="/feed" element={<Feed />} />
                  <Route
                    path="/students/:id"
                    element={<PlayerPage playerType="students" />}
                  />
                  <Route
                    path="/teachers/:id"
                    element={<PlayerPage playerType="teachers" />}
                  />
                  <Route path="/faq" element={<Faq />} />
                  <Route element={<IsPrefect />}>
                    <Route
                      path="/students/:id/add-points"
                      element={<AddPoints playerType="students" />}
                    />
                    <Route
                      path="/teachers/:id/add-points"
                      element={<AddPoints playerType="teachers" />}
                    />
                    <Route path="/guide" element={<Guide />} />
                    <Route path="/guide/start" element={<Guide start />} />
                  </Route>
                </Route>
              </Route>
              <Route element={<AuthSimpleLayout />}>
                <Route
                  path="/account/reset-password"
                  element={<PasswordReset />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Error404 />} />
          </Routes>
        </UserContextProvider>
      </QueryClientProvider>
      <ToastContainer
        position="top-right"
        theme="light"
        limit={5}
        autoClose={2500}
      />
    </Router>
  );
};

export default App;
